<template>
	<div :class="['booking-map', {'is-disabled' : !mapIsVisible}]">
		<transition name="fade-slow">
			<GmapMap
				v-show="mapIsVisible"
				ref="gmap"
				:options="mapOptions"
				:center="{lat:0, lng:0}"
				:zoom="7"
				:clickableIcons="false"
			>

				<!-- Custom markers -->
				<FlytMapMarker
					v-for="(m, i) in markers"
					ref="markers"
					:location="m"
					:key="i"
					:clickable="true"
					:opacity="setMarkerOpacity(m)"
					@mouseover="markerMouseOver(m)"
					@mouseout="markerMouseOut()"
					@click="onMarkerClick(m)"
				/>

				<!-- Completed journey path -->
				<GmapPolyline v-if="showJourney" :path="journeyPath" :options="lineOptions" />
			</GmapMap>
		</transition>
	</div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from "vuex"
import { mapOptions } from "./mapOptions"
import { gmapApi } from "vue2-google-maps"
import FlytMapMarker from "@/components/booking/map/FlytMapMarker"

export default {
	data() {
		return {
			mapBounds: null,
			showJourney: false,
			journeyPath: [],
			hovered: null,
			mapOptions,
			markerOpacity: 1,
			lineOptions: {
				strokeColor: '#5bc1b8',
				strokeWeight: 2,
				geodesic: true
			}
		}
	},

	name: "BookingMap",

	components: { FlytMapMarker },

	mounted() {
		// Store the css selector for the map
		this.$store.commit("map/setCssSelector", `.${this.$refs.gmap.$el.className}`)
	},

	computed: {
		...mapGetters("bookingForm", ["defaultDestinationLocationSet"]),
		...mapGetters("browser", ["isMobile"]),
		...mapGetters("map", [
			"hoveredLocationID",
			"padding",
			"searchingMarkers",
			"journeyMarkers"
		]),

		/**
		 * So that we can reference the google maps api directly
		 */
		gmapApi,

		/**
		 * Check if searching markers are present
		 */
		hasSearchingMarkers() {
			return this.searchingMarkers.length !== 0
		},

		/**
		 * Check if journey markers have been set
		 */
		hasJourneyMarkers() {
			return this.journeyMarkers.length !== 0
		},

		/**
		 * Returns an array of Google Map compatible markers
		 * with a priority on all search-related locations.
		 */
		markers() {
			if (this.hasSearchingMarkers) return this.gmapMarkersFromArray(this.searchingMarkers)
			if (this.hasJourneyMarkers) return this.gmapMarkersFromArray(this.journeyMarkers)
			return []
		},

		noMarkersHovered() {
			return this.hoveredLocationID === null
		},

		/**
		 * Map should only be visible if there are markers
		 */
		mapIsVisible() {
			return !this.isMobile && 
				((!this.defaultDestinationLocationSet && this.markers.length > 0) || (this.defaultDestinationLocationSet && this.markers.length > 1))
		}
	},

	watch: {
		/**
		 * After each marker update, reset the map bounds to fit
		 */
		markers() {
			this.updateJourneyPath()
			this.fitBoundsToMarkers()
		},

		/**
		 * Toggle the navbar when mapIsVisible changes
		 */
		mapIsVisible() {
			this.$navbar.classList.toggle("is-map-open", this.mapIsVisible)
		}
	},

	methods: {
		...mapActions("bookingForm", ["selectLocationByID"]),
		...mapMutations("map", [
			"setHoveredLocationID",
			"clearHoveredLocationID"
		]),

		/**
		 * If new markers are found repopulate the mapMarkers array
		 * and set map boundaries to the corresponding locations.
		 */
		gmapMarkersFromArray(markers) {
			if (!this.gmapApi || !this.gmapApi.maps) {
				console.error("Google Maps API not loaded")
				setTimeout(() => {
					this.gmapMarkersFromArray(markers);
				}, 200); // Retry after a short delay
				return;
			}
			this.mapBounds = new this.gmapApi.maps.LatLngBounds()
			return markers.map(marker => {
				let position = new this.gmapApi.maps.LatLng(marker.lat, marker.lng)
				this.mapBounds.extend(position)
				let gmapMarker = new this.gmapApi.maps.Marker({ position })
				gmapMarker.location_id = marker.id
				gmapMarker.location_type = marker.locationType
				gmapMarker.is_private = marker.isPrivate
				return gmapMarker
			})
		},

		/**
		 * Return 1 if no markers are being hovered OR
		 * the current marker is, otherwise return 0.5
		 */
		setMarkerOpacity(marker) {
			return (this.noMarkersHovered || this.hoveredLocationID === marker.location_id)
				? 1
				: 0.5
		},

		/**
		 * Update the hovered location of search-related markers
		 */
		markerMouseOver(marker) {
			if (!this.hasSearchingMarkers)
				return

			this.setHoveredLocationID(marker.location_id)
		},

		/**
		 * Once markers are updated, fit map bounds
		 */
		fitBoundsToMarkers() {
			if (this.markers?.length > 0 && this.mapBounds !== null)
				this.$nextTick(() => this.$refs.gmap.fitBounds(this.mapBounds, this.padding))
		},

		markerMouseOut() {
			this.clearHoveredLocationID()
		},

		/**
		 * Emit event on the root object (not ideal, but there's no clean
		 * way to communicate between the map and location components).
		 * Will only run if the visible markers are search-related.
		 */
		onMarkerClick({location_id, location_type}) {
			if (!this.hasSearchingMarkers)
				return

			this.$root.$emit('selectLocationByID', location_id, location_type)
		},

		/**
		 * Sets a single line from list of locations and displays it on the map
		 */
		updateJourneyPath() {
			if (!this.hasJourneyMarkers || this.journeyMarkers.length < 2) {
				this.showJourney = false
				return
			}

			this.journeyPath = this.markers.map(marker => {
				return {
					lat: marker.position.lat(),
					lng: marker.position.lng()
				}
			})
			this.showJourney = true
		},
	},
}
</script>

<style lang="scss" scoped>
// Booking map root wrapper
.booking-map {
	height: 100%;
	left: 0;
	position: absolute;
	top: 0;
	width: 100%;
	will-change: opacity;
	&.is-disabled {
		pointer-events: none;
	}

	/deep/ .vue-map-container {
		bottom: 0;
		height: 100%;
		margin-bottom: -12px; // Obscure smallprint
		max-height: 100vh;
		position: sticky;
		top: 0;
		width: 100%;
		will-change: opacity;
	}

	/deep/ .vue-map {
		height: 100%;
	}
}
</style>
